import React, { useEffect, useRef, useState } from 'react';
import './CelebrationEffect.css';

function getRatingIconUrl(rating) {
  const value = Number(rating) || 0;
  let iconNumber;
  if (value <= 1.9) iconNumber = 1;
  else if (value <= 2.9) iconNumber = 2;
  else if (value <= 3.9) iconNumber = 3;
  else if (value <= 4.79) iconNumber = 4;
  else iconNumber = 5;
  return `${process.env.PUBLIC_URL}/assets/ratings/TCM-RatingDisplay-${iconNumber}.png`;
}

function CelebrationEffect({ map, restroom, rating, onDone }) {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const pulseTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const onDoneRef = useRef(onDone);
  const [confettiStart, setConfettiStart] = useState(null);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    let overlay = null;
    let startPosSet = false;
    const pulseDiv = document.createElement('div');
    pulseDiv.className = 'restroom-celebration-pulse';

    class PulseOverlay extends window.google.maps.OverlayView {
      constructor() {
        super();
        this.div = pulseDiv;
      }
      onAdd() {
        const panes = this.getPanes();
        if (panes) panes.overlayLayer.appendChild(this.div);
      }
      draw() {
        const projection = this.getProjection();
        if (!projection) return;
        const point = projection.fromLatLngToDivPixel({
          lat: restroom.latitude,
          lng: restroom.longitude
        });
        if (!point) return;
        this.div.style.left = `${point.x}px`;
        this.div.style.top = `${point.y}px`;
        if (!startPosSet) {
          const rect = this.div.getBoundingClientRect();
          if (rect.width > 0) {
            startPosSet = true;
            setConfettiStart({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
          }
        }
      }
      onRemove() {
        if (this.div && this.div.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
      }
    }

    overlay = new PulseOverlay();
    overlay.setMap(map);

    pulseTimerRef.current = setTimeout(() => {
      if (overlay) overlay.setMap(null);
      overlay = null;
    }, 3500);

    return () => {
      clearTimeout(pulseTimerRef.current);
      if (overlay) overlay.setMap(null);
    };
  }, [map, restroom.latitude, restroom.longitude]);

  useEffect(() => {
    if (!confettiStart) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // `rating` (the rating just given) takes precedence so the celebration
    // shows the poop icon for what the user rated, not the restroom's average.
    const poopImage = new Image();
    poopImage.src = getRatingIconUrl(rating ?? restroom.averageRating ?? restroom.rating);

    const PARTICLE_COUNT = 80;
    const particles = [];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = Math.PI + Math.random() * Math.PI + (Math.random() - 0.5) * 1.2;
      const speed = 4 + Math.random() * 9;
      particles.push({
        x: confettiStart.x,
        y: confettiStart.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        gravity: 0.12 + Math.random() * 0.08,
        size: 16 + Math.random() * 14,
        rotation: Math.random() * Math.PI,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        life: 1,
        decay: 0.005 + Math.random() * 0.005
      });
    }

    const DURATION = 3200;
    const startTime = performance.now();

    const draw = (now) => {
      const elapsed = now - startTime;
      const progress = elapsed / DURATION;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const imageReady = poopImage.complete && poopImage.naturalWidth > 0;
      for (const particle of particles) {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += particle.gravity;
        particle.vx *= 0.99;
        particle.rotation += particle.rotationSpeed;
        particle.life -= particle.decay;
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate(particle.rotation);
        ctx.globalAlpha = Math.max(particle.life, 0);
        if (imageReady) {
          ctx.drawImage(poopImage, -particle.size / 2, -particle.size / 2, particle.size, particle.size);
        } else {
          ctx.fillStyle = '#FFC107';
          ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        }
        ctx.restore();
      }
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (onDoneRef.current) onDoneRef.current();
      }
    };

    const startAnimation = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
    };

    if (poopImage.complete && poopImage.naturalWidth > 0) {
      startAnimation();
    } else {
      poopImage.onload = startAnimation;
      fallbackTimerRef.current = setTimeout(startAnimation, 400);
    }

    return () => {
      clearTimeout(fallbackTimerRef.current);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [confettiStart, rating, restroom.averageRating, restroom.rating]);

  return <canvas ref={canvasRef} className="restroom-confetti-canvas" />;
}

export default CelebrationEffect;
